import { action, makeObservable, observable, computed, runInAction } from 'mobx';
import { Message, StoreNode, StoreNodeProps } from '../../store';
import { Store } from '../../store/store';
import { WindowState } from '../overlays';
import { notifyLoading, notifyError, notifySuccess } from '../../services/notifications';
import { JobModel } from '../../entities/job';
import { DeleteMomentInput } from '@clipr/lib';

export type RemoveAccessOpenParams = {
  jobId: string
  momentIds: string[],
  onSubmitCallback?: () => void
}

export class DeleteMomentsPopupState
  extends StoreNode {

  readonly nodeType = 'DeleteMomentsPopup';

  constructor(store: Store, props?: StoreNodeProps) {
    super(store, props);
    makeObservable<this, 'handleSubmitError'>(this, {
      isLoading: observable,
      jobId: observable,
      targetIds: observable,
      onSubmitCallback: observable,
      job: computed,
      hasMultipleTargets: computed,
      submit: action,
      handleSubmitDeleteMoments: action,
      handleSubmitError: action,
      open: action,
      close: action,
    });
    this.window.listen(
      this.windowListener);
  }
  isLoading: boolean = false;

  jobId: string | null = null;
  targetIds: string[] | null = null;
  onSubmitCallback: (() => void) | null = null;

  get job(): JobModel | null {
    return this.store.jobManager.maybeGetJob(this.jobId);
  }

  get hasMultipleTargets() {
    return this.targetIds && this.targetIds.length > 1;
  }

  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        if (this.isLoading)
          return;
        this.close('close');
        break;
    }
  }
  readonly window = new WindowState(this.store);

  async submit() {
    if (!this.jobId) return;

    await this.handleSubmitDeleteMoments();

    runInAction(() => {
      this.close('momentsDeleted');
      if (this.onSubmitCallback)
        this.onSubmitCallback();
    });

    runInAction(() => this.isLoading = false)
  }

  async handleSubmitDeleteMoments() {
    const { job } = this;
    if (!job || !this.targetIds)
      return;

    this.isLoading = true;
    notifyLoading(this, "Deleting clips");

    let hasConflicts = false;
    const input: DeleteMomentInput[] = this.targetIds.map(momentId => {
      const moment = job.getMoment(momentId);
      if (moment?.hasChildSubtopics)
        hasConflicts = true;

      return {
        jobId: job.id,
        momentId: momentId
      }
    }).filter(el => el) as DeleteMomentInput[];

    if (job.isDone && hasConflicts) {
      this.handleSubmitError('Could not delete the topics because of underlying subtopics.')
      return;
    }

    const [, err] = await job.apiDeleteMoments(input);
    if (err) {
      this.handleSubmitError('Could not delete the clips because of an error.')
      return;
    }

    notifySuccess(this, 'Clips were deleted.');
  }


  private handleSubmitError(msg: string) {
    notifyError(this, msg);
    this.isLoading = false;
  }

  open(params: RemoveAccessOpenParams) {
    this.dispatch('Overlays', 'openWindow', { name: 'DeleteMomentsPopup' });

    this.jobId = params.jobId;
    this.targetIds = params.momentIds || null;
    this.onSubmitCallback = params.onSubmitCallback || null;
  }

  close(msg?: string) {
    this.dispatch('Overlays', 'closeWindow');

    this.jobId = null;
    this.targetIds = null;

    if (msg) this.emit(msg);
  }
}